import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import {
  EMCType,
  EmcOptions as SharedEmcOptions,
  EmcSearchOutput,
  EntityModelCustomizationService as SharedEmcService,
  PermissionsService,
} from 'shared-lib';
import { PoleService } from '../../../services/pole.service';
import { EntityModelCustomizationService } from '../../../services/entity-model-customization.service';
import { ChecklistItem } from '../../../models/checklist-item.model';
import {
  EmcOptions,
  Purpose,
  SectionDefinitionEntityType,
  TableListPage,
} from '../../../models/dynamic-buildings.model';
import {
  DynamicPoleFilter,
  POLE_CONDITIONS,
  POLE_VALIDATION_STATUSES,
} from '../../../models/dynamic-poles.model';

@Component({
  selector: 'app-dynamic-poles',
  templateUrl: './dynamic-poles.component.html',
  styleUrls: ['../../dynamic-buildings/dynamic-buildings.component.scss'],
})
export class DynamicPolesComponent implements OnInit, OnDestroy {
  filter: DynamicPoleFilter = new DynamicPoleFilter();
  cardsFilter: DynamicPoleFilter = new DynamicPoleFilter();

  cardEmc: SharedEmcOptions[] = [];
  listPageEmc: EmcOptions[] = [];
  filterEmc: SharedEmcOptions[] = [];

  entityType: EMCType = EMCType.Pole;

  tableListPage: TableListPage = new TableListPage();
  visibleColumns: { label: string; index: number }[] = [];

  pageSize: number = 10;
  currentPage: number = 1;
  count: number = 0;

  checklistItems: ChecklistItem[] = [];
  isFilterWindowOpened: boolean = false;
  utilityId: number = 0;

  poleTypes: { key: number; value: string }[] = [];

  private ngUnsubscribe = new Subject<void>();
  private storageKey = '';

  constructor(
    private poleService: PoleService,
    private emcService: EntityModelCustomizationService,
    private sharedEmcService: SharedEmcService,
    private toastr: ToastrService,
    private router: Router,
    public permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '0');
    this.storageKey = `filterDynamicPoles|${this.utilityId}`;

    const cachedFilters = localStorage.getItem(this.storageKey);
    if (cachedFilters) {
      this.filter = JSON.parse(cachedFilters);
      this.currentPage = this.filter.pageInfo.page ?? 1;
      localStorage.removeItem(this.storageKey);
    }
    this.applyDtFilterFromStorage();
    this.cardsFilter = this.buildRequest();

    this.loadEmcConfig();
    this.loadPoleTypes();
    this.loadList();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // Transformer details and network structure open this page with a DT scope in localStorage
  private applyDtFilterFromStorage() {
    const fp = this.filter.filterParams;
    const dtIds = localStorage.getItem('dtIds');
    if (dtIds) {
      fp.dtIds = JSON.parse(dtIds);
      fp.dtName = localStorage.getItem('assetName') || '';
      localStorage.removeItem('dtIds');
      localStorage.removeItem('assetName');
    }
    const dtId = parseInt(localStorage.getItem('dtId') || '0');
    const dtName = localStorage.getItem('dtName') || '';
    if (dtId && dtName) {
      fp.dtId = dtId;
      fp.dtName = dtName;
      localStorage.removeItem('dtId');
      localStorage.removeItem('dtName');
    }
  }

  private loadEmcConfig() {
    const entity = SectionDefinitionEntityType.Pole;
    this.emcService.getSelectedEmcOptions(entity, Purpose.Card, this.utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => { this.cardEmc = r.body?.data ?? []; },
    });
    this.emcService.getSelectedEmcOptions(entity, Purpose.ListPage, this.utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.listPageEmc = (r.body?.data ?? []).sort((a: EmcOptions, b: EmcOptions) => a.displayOrder - b.displayOrder);
        this.buildVisibleColumns();
      },
    });

    this.sharedEmcService.getSelectedEmcOptionsForFilter(entity, Purpose.Filter).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.filterEmc = r.body?.data ?? [];
        this.createFilterCards();
      },
    });
  }

  private loadPoleTypes() {
    this.poleService.getPoleTypesForOptions().pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.poleTypes = r?.data ?? [];
        this.createFilterCards();
      },
    });
  }

  loadList() {
    this.filter.pageInfo.page = this.currentPage;
    this.filter.pageInfo.pageSize = this.pageSize;

    this.poleService.getAllDynamic(this.buildRequest()).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.tableListPage = data.data ?? new TableListPage();
          this.count = this.tableListPage.count ?? 0;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  private buildRequest(): DynamicPoleFilter {
    const request: DynamicPoleFilter = JSON.parse(JSON.stringify(this.filter));
    if (request.filterParams.dateTo) {
      request.filterParams.dateTo = `${request.filterParams.dateTo}T23:59:59`;
    }
    return request;
  }

  pageChange(page: number) {
    this.currentPage = page;
    this.loadList();
  }

  onSearchOutput(obj: EmcSearchOutput) {
    this.filter.searchObject = {
      attributeDefinitionId: obj.attributeDefinitionId ?? null,
      staticPropertyName: obj.staticPropertyName ?? '',
      searchValue: obj.searchValue ?? '',
    };
    this.createFilterCards();
    this.applyFilter();
  }

  openFilterWindow() { this.isFilterWindowOpened = true; }
  closeFilterWindow() { this.isFilterWindowOpened = false; }

  onAddFilter() {
    this.createFilterCards();
    this.applyFilter();
  }

  applyFilter() {
    this.currentPage = 1;
    this.cardsFilter = this.buildRequest();
    this.loadList();
  }

  resetFilter() {
    this.filter = new DynamicPoleFilter();
    this.checklistItems = [];
    this.applyFilter();
  }

  removeFilter(item: ChecklistItem) {
    const fp = this.filter.filterParams;
    if (item.property.startsWith('attr:')) {
      const attrId = Number(item.property.split(':')[1]);
      const df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
      if (df) {
        const option = this.getAttributeDefinition(attrId)?.optionDefinitions.find(o => o.value === item.selectedValue);
        if (option) df.selectedOptions = df.selectedOptions.filter(id => id !== option.id);
        else { df.from = null; df.to = null; }
        if (df.selectedOptions.length === 0 && !df.from && !df.to) {
          this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f !== df);
        }
      }
    }
    switch (item.property) {
      case 'Search':
        this.filter.searchObject = { attributeDefinitionId: null, staticPropertyName: '', searchValue: '' };
        break;
      case 'Date From':
        fp.dateFrom = null;
        break;
      case 'Date To':
        fp.dateTo = null;
        break;
      case 'Validation Status':
        fp.validationStatuses = fp.validationStatuses.filter(s => this.validationStatusLabel(s) !== item.valueTranslation);
        break;
      case 'Review Status':
        fp.reviewStatus = null;
        break;
      case 'Pole Type':
        fp.type = 0;
        break;
      case 'Pole Condition':
        fp.condition = 0;
        break;
      case 'DT':
        fp.dtId = null;
        fp.dtIds = [];
        fp.dtName = '';
        break;
      case 'Region':
        fp.selectedRegionModel = null;
        fp.selectedRegions = [];
        fp.selectedAreaModels = [];
        fp.selectedAreas = [];
        break;
      case 'District':
        fp.selectedAreaModels = fp.selectedAreaModels.filter(a => a.name !== item.selectedValue);
        fp.selectedAreas = fp.selectedAreaModels.map(a => a.id);
        break;
      case 'Geographical Region':
        fp.selectedGeoRegionModel = null;
        fp.selectedGeographicalRegions = [];
        fp.selectedSubGeoModels = [];
        fp.selectedSubGeographicalRegions = [];
        break;
      case 'Sub-Geographical Region':
        fp.selectedSubGeoModels = fp.selectedSubGeoModels.filter(s => s.name !== item.selectedValue);
        fp.selectedSubGeographicalRegions = fp.selectedSubGeoModels.map(s => s.id);
        break;
      case 'User':
        fp.selectedUserModels = fp.selectedUserModels.filter(u => u.name !== item.selectedValue);
        fp.selectedUsers = fp.selectedUserModels.map(u => u.id);
        break;
    }
    this.createFilterCards();
    this.applyFilter();
  }

  private createFilterCards() {
    const items: ChecklistItem[] = [];
    const fp = this.filter.filterParams;

    if (this.filter.searchObject.searchValue) {
      items.push(new ChecklistItem('Search', this.filter.searchObject.searchValue, 'COMMON.SEARCH', this.filter.searchObject.searchValue));
    }
    if (fp.dateFrom) items.push(new ChecklistItem('Date From', fp.dateFrom, 'COMMON.DATE_FROM', fp.dateFrom));
    if (fp.dateTo) items.push(new ChecklistItem('Date To', fp.dateTo, 'COMMON.DATE_TO', fp.dateTo));
    fp.validationStatuses.forEach(s => {
      const label = this.validationStatusLabel(s);
      items.push(new ChecklistItem('Validation Status', label, 'COMMON.VALIDATION_STATUS', label));
    });
    if (fp.reviewStatus != null) {
      const label = fp.reviewStatus ? 'COMMON.REVIEW_STATUS_OPTIONS.REVIEWED' : 'COMMON.REVIEW_STATUS_OPTIONS.NOT_REVIEWED';
      items.push(new ChecklistItem('Review Status', label, 'COMMON.REVIEW_STATUS', label));
    }
    if (fp.type) {
      const label = this.poleTypes.find(t => t.key == fp.type)?.value ?? fp.type.toString();
      items.push(new ChecklistItem('Pole Type', label, 'COMMON.TABLE_HEADERS.POLE_TYPE', label));
    }
    if (fp.condition) {
      const label = POLE_CONDITIONS.find(c => c.value == fp.condition)?.translation ?? fp.condition.toString();
      items.push(new ChecklistItem('Pole Condition', label, 'COMMON.POLE.CONDITION.TITLE', label));
    }
    if (fp.dtName) {
      items.push(new ChecklistItem('DT', fp.dtName, fp.dtIds.length > 0 ? 'COMMON.NETWORKASSETFILTER' : 'COMMON.DT_NAME', fp.dtName));
    }
    if (fp.selectedRegionModel) {
      items.push(new ChecklistItem('Region', fp.selectedRegionModel.name, 'COMMON.BU.SINGLE', fp.selectedRegionModel.name));
    }
    fp.selectedAreaModels.forEach(a => items.push(new ChecklistItem('District', a.name, 'COMMON.UT.SINGLE', a.name)));
    if (fp.selectedGeoRegionModel) {
      items.push(new ChecklistItem('Geographical Region', fp.selectedGeoRegionModel.name, 'COMMON.BU.SINGLE', fp.selectedGeoRegionModel.name));
    }
    fp.selectedSubGeoModels.forEach(s => items.push(new ChecklistItem('Sub-Geographical Region', s.name, 'COMMON.UT.SINGLE', s.name)));
    fp.selectedUserModels.forEach(u => items.push(new ChecklistItem('User', u.name, 'COMMON.USER.SINGLE', u.name)));

    this.filter.dynamicFilters.forEach(df => {
      const attr = this.getAttributeDefinition(df.attributeDefinitionId!);
      if (!attr) return;
      const property = `attr:${attr.id}`;
      df.selectedOptions.forEach(optionId => {
        const value = attr.optionDefinitions.find(o => o.id === optionId)?.value ?? optionId.toString();
        items.push(new ChecklistItem(property, value, attr.label, value));
      });
      if (df.from || df.to) {
        const range = `${df.from ? new Date(df.from).toLocaleDateString() : ''} - ${df.to ? new Date(df.to).toLocaleDateString() : ''}`;
        items.push(new ChecklistItem(property, range, attr.label, range));
      }
    });

    this.checklistItems = items;
  }

  private getAttributeDefinition(attrId: number) {
    return this.filterEmc.find(x => x.attributeDefinitionId === attrId)?.attributeDefinition;
  }

  private validationStatusLabel(status: number): string {
    return POLE_VALIDATION_STATUSES.find(s => s.value === status)?.translation ?? status.toString();
  }

  download() {
    this.poleService.downloadFileDynamic(this.buildRequest()).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') window.location.href = data.data;
        else if (data?.status === 'Pending' || data?.status === '600') this.toastr.info(data?.message || '');
        else this.toastr.error(data?.message || 'Request failed');
      },
      error: () => this.toastr.error('Request failed'),
    });
  }

  onRowClick(guid: string) {
    if (!guid) return;
    localStorage.setItem(this.storageKey, JSON.stringify(this.filter));
    this.router.navigate([`/poles/edit/${guid}`]);
  }

  private buildVisibleColumns() {
    this.visibleColumns = this.listPageEmc
      .map((emc, index) => ({ emc, index }))
      .filter(({ emc }) => this.isColumnVisible(emc.staticPropertyName))
      .map(({ emc, index }) => ({ label: emc.labelName, index }));
  }

  private isColumnVisible(staticName: string | null): boolean {
    switch (staticName) {
      case 'ValidationStatus':
        return this.permissionsService.isValidationStatusAllowed;
      case 'ReviewStatus':
        return this.permissionsService.isReviewStatusAllowed;
      default:
        return true;
    }
  }
}
